// src/games/Sudoku/SudokuContext.tsx
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
  SudokuCell,
  canRevealSudoku,
  clearSudoku,
  isSudokuFilled,
  revealAllSudoku,
  revealSudokuCell,
  scoreSudoku,
  setSudokuValue,
} from './rules';

interface SetValueArgs {
  x: number;
  y: number;
  newValue: string;
}

interface ContextProps {
  matrix: SudokuCell[][];
  width: number;
  height: number;
  score: number;
  isSudokuFinished: boolean;
  isBoardFilled: boolean;
  focus: { x: number; y: number } | null;
  load: (cells: SudokuCell[][]) => void;
  setIsSudokuFinished: (finished: boolean) => void;
  setValuesSquares: (args: SetValueArgs) => void;
  checkFocus: (x: number, y: number) => void;
  /** Mantido para o Square; tabuleiro completo não termina mais o jogo sozinho. */
  verifyFinish: () => void;
  canRevealFocused: () => boolean;
  revealFocused: () => SudokuCell[][];
  revealAll: () => SudokuCell[][];
  clear: () => void;
}

const SudokuContext = createContext({} as ContextProps);

const SudokuProvider: React.FC = ({ children }) => {
  const [matrix, setMatrix] = useState<SudokuCell[][]>([]);
  const [isSudokuFinished, setIsSudokuFinished] = useState(false);
  const [focus, setFocus] = useState<{ x: number; y: number } | null>(null);

  const load = useCallback((cells: SudokuCell[][]) => {
    setMatrix(cells);
    setFocus(null);
  }, []);

  const setValuesSquares = useCallback(({ x, y, newValue }: SetValueArgs) => {
    setMatrix((current) => setSudokuValue(current, x, y, newValue));
  }, []);

  const checkFocus = useCallback((x: number, y: number) => setFocus({ x, y }), []);
  const verifyFinish = useCallback(() => undefined, []);

  const canRevealFocused = useCallback(
    () => focus !== null && canRevealSudoku(matrix, focus.x, focus.y),
    [focus, matrix]
  );

  const revealFocused = useCallback(() => {
    if (!focus) return matrix;
    const next = revealSudokuCell(matrix, focus.x, focus.y);
    setMatrix(next);
    return next;
  }, [focus, matrix]);

  const revealAll = useCallback(() => {
    const next = revealAllSudoku(matrix);
    setMatrix(next);
    return next;
  }, [matrix]);

  const clear = useCallback(() => setMatrix((current) => clearSudoku(current)), []);

  const value = useMemo<ContextProps>(
    () => ({
      matrix,
      width: matrix[0]?.length ?? 0,
      height: matrix.length,
      score: scoreSudoku(matrix),
      isSudokuFinished,
      isBoardFilled: isSudokuFilled(matrix),
      focus,
      load,
      setIsSudokuFinished,
      setValuesSquares,
      checkFocus,
      verifyFinish,
      canRevealFocused,
      revealFocused,
      revealAll,
      clear,
    }),
    [matrix, isSudokuFinished, focus, load, setValuesSquares, checkFocus, verifyFinish, canRevealFocused, revealFocused, revealAll, clear]
  );

  return <SudokuContext.Provider value={value}>{children}</SudokuContext.Provider>;
};

function useSudoku(): ContextProps {
  return useContext(SudokuContext);
}

export { SudokuProvider, useSudoku };
